/**
 * <diwali-decor> — measures a decorated heading so the Diwali decoration can follow the title's real line layout.
 *
 * CSS alone can't know where a wrapped title's first line ends, how wide its widest line is, or whether the wide layout (diyas in
 * the text flow, fireworks hanging beyond them) has room. This element measures the heading and:
 *
 *  - Chooses the layout. Below 1366px the compact layout always applies. From 1366px the wide layout is tried first; if it doesn't
 *    fit (the title would wrap, the right diya would drop onto its own line, or a firework would leave the screen) the heading is
 *    switched to the compact layout instead, so the title simply breaks onto the next line with the diyas still aligned.
 *  - For the compact layout, publishes the measured lines as CSS custom properties on the heading, in px relative to its top-left:
 *      --dd-first-left / --dd-first-right / --dd-first-top : the first line's start, end and top
 *      --dd-left / --dd-right : the widest extent of any line
 *      --dd-mid : vertical centre of the whole text block
 *
 * The compact layout is flagged with `data-diwali-ready` on the heading; the wide layout has no attribute. Without JS the
 * stylesheet's media queries pick the layout (compact below 1366px, wide above). Re-measures on resize and after web fonts load.
 */
if (!customElements.get('diwali-decor')) {
  const WIDE = window.matchMedia('(min-width: 1366px)');
  const EDGE_MARGIN = 8;

  class DiwaliDecor extends HTMLElement {
    connectedCallback() {
      this.heading = this.closest('h1, h2, h3, h4, h5, h6');
      if (!this.heading) return;

      this.measure = this.measure.bind(this);
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.heading);
      document.fonts?.ready.then(this.measure);
      this.measure();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    /** Visual lines of the title text (everything between the two decor elements), relative to the heading's top-left. */
    readLines() {
      const heading = this.heading;
      const left = heading.querySelector(':scope > .diwali-decor--left');
      const right = heading.querySelector(':scope > .diwali-decor--right');
      if (!left || !right) return null;

      const range = document.createRange();
      range.setStartAfter(left);
      range.setEndBefore(right);

      const rects = [...range.getClientRects()].filter((rect) => rect.width > 0 && rect.height > 0);
      if (!rects.length) return null;

      const origin = heading.getBoundingClientRect();
      const lines = rects.map((rect) => ({
        left: rect.left - origin.left,
        right: rect.right - origin.left,
        top: rect.top - origin.top,
        bottom: rect.bottom - origin.top,
      }));

      return { left, right, origin, lines };
    }

    /** True when the wide layout sits on a single line and stays on screen. */
    wideLayoutFits({ left, right, origin, lines }) {
      const firstTop = Math.min(...lines.map((line) => line.top));
      const lastBottom = Math.max(...lines.map((line) => line.bottom));
      const wraps = lines.some((line) => Math.abs(line.top - firstTop) >= 2);

      // The right diya must sit on the text's line, not have dropped below it.
      const rightDropped = right.getBoundingClientRect().top - origin.top >= lastBottom;

      const leftBurst = left.querySelector('.diwali-decor__fireworks')?.getBoundingClientRect();
      const rightBurst = right.querySelector('.diwali-decor__fireworks')?.getBoundingClientRect();
      const viewport = document.documentElement.clientWidth;
      const onScreen =
        (!leftBurst || leftBurst.left >= EDGE_MARGIN) && (!rightBurst || rightBurst.right <= viewport - EDGE_MARGIN);

      return !wraps && !rightDropped && onScreen;
    }

    measure() {
      const heading = this.heading;

      // Wide screens: try the CSS-only wide layout first.
      if (WIDE.matches) {
        heading.removeAttribute('data-diwali-ready');
        const wide = this.readLines();
        if (wide && this.wideLayoutFits(wide)) return;
      }

      // Compact layout (narrow screen, or the wide layout didn't fit).
      heading.setAttribute('data-diwali-ready', '');
      const compact = this.readLines();
      if (!compact) {
        heading.removeAttribute('data-diwali-ready');
        return;
      }

      const { lines } = compact;
      const firstTop = Math.min(...lines.map((line) => line.top));
      const firstLine = lines.filter((line) => Math.abs(line.top - firstTop) < 2);

      const values = {
        '--dd-first-left': Math.min(...firstLine.map((line) => line.left)),
        '--dd-first-right': Math.max(...firstLine.map((line) => line.right)),
        '--dd-first-top': firstTop,
        '--dd-left': Math.min(...lines.map((line) => line.left)),
        '--dd-right': Math.max(...lines.map((line) => line.right)),
        '--dd-mid': (firstTop + Math.max(...lines.map((line) => line.bottom))) / 2,
      };

      for (const [name, value] of Object.entries(values)) {
        heading.style.setProperty(name, `${value.toFixed(1)}px`);
      }
    }
  }

  customElements.define('diwali-decor', DiwaliDecor);
}
